import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, type User } from 'firebase/auth';
import { auth } from '../services/firebase';
import api from '../services/api';
import { setAnalyticsUserProperties } from '../utils/firebaseAnalytics';
import { shouldLogVerbose } from '../utils/env';

interface BackendUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  loyaltyNumber: string;
  role?: string;
  storeId?: string | null;
  clubId?: string | null;
  passwordResetRequired?: boolean;
}

interface AuthContextType {
  user: User | null;
  backendUser: BackendUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [backendUser, setBackendUser] = useState<BackendUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch backend user data when Firebase user changes
  useEffect(() => {
    const fetchBackendUser = async () => {
      if (user) {
        try {
          const response = await api.get('/users/profile');
          const backendUserData = response.data;
          
          // CRITICAL: Validate that response is actually JSON, not HTML
          if (typeof backendUserData === 'string' || !backendUserData || typeof backendUserData !== 'object') {
            console.error('🚨 CRITICAL: API returned invalid response (likely HTML error page):', {
              dataType: typeof backendUserData,
              dataPreview: typeof backendUserData === 'string' ? backendUserData.substring(0, 200) : backendUserData,
              status: response.status,
              headers: response.headers
            });
            throw new Error('API returned invalid response format');
          }
          
          // CRITICAL: Verify Firebase user email matches backend user email
          // This catches session mismatches early
          if (user.email && backendUserData.email && user.email !== backendUserData.email) {
            console.error('🚨 CRITICAL: Firebase user email does not match backend user email!', {
              firebaseEmail: user.email,
              firebaseUid: user.uid,
              backendEmail: backendUserData.email,
              backendUserId: backendUserData.id,
              backendStoreId: backendUserData.storeId
            });
            // Don't set backendUser if emails don't match - force re-auth
            setBackendUser(null);
            // Sign out to clear the mismatch
            await signOut(auth);
            return;
          }
          
          setBackendUser(backendUserData);
          
          // Set Firebase Analytics user properties (exclude SUPERADMIN)
          if (backendUserData.role && backendUserData.role !== 'SUPERADMIN') {
            await setAnalyticsUserProperties(backendUserData.id, backendUserData.role);
          }
        } catch (error) {
          console.error('Failed to fetch backend user:', error);
          setBackendUser(null);
        }
      } else {
        setBackendUser(null);
      }
    };

    fetchBackendUser();
  }, [user]);

  useEffect(() => {
    let previousUid: string | null = null;
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Log auth state changes in development and staging
      if (shouldLogVerbose()) {
        console.log('🔄 Firebase auth state changed:', {
          hasUser: !!firebaseUser,
          email: firebaseUser?.email,
          uid: firebaseUser?.uid,
          previousUid
        });
      }
      
      // CRITICAL: If user changed (different UID), clear old session
      if (firebaseUser && previousUid && firebaseUser.uid !== previousUid) {
        console.warn('⚠️ Firebase user UID changed - clearing old session', {
          previousUid,
          newUid: firebaseUser.uid
        });
        // User changed, clear old state
        setBackendUser(null);
        // Sign out to force fresh login
        try {
          await signOut(auth);
          localStorage.clear();
          sessionStorage.clear();
        } catch (error) {
          console.error('Error clearing changed user:', error);
        }
        setUser(null);
        setLoading(false);
        previousUid = null;
        return;
      }
      
      previousUid = firebaseUser?.uid || null;
      setUser(firebaseUser);
      setLoading(false);
    });

    return unsubscribe;
  }, []); // Empty deps - only run once on mount

  const login = async (email: string, password: string) => {
    // Log login attempts in development and staging
    if (shouldLogVerbose()) {
      console.log('🔐 Logging in:', email);
    }
    
    // CRITICAL: Clear any existing auth state before logging in
    // This prevents stale sessions from interfering
    try {
      // Sign out any existing user first
      if (auth.currentUser) {
        if (shouldLogVerbose()) {
          console.log('⚠️ Existing user found, signing out first:', auth.currentUser.email);
        }
        await signOut(auth);
        // Small delay to ensure signOut completes
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Clear storage to remove any stale tokens
      localStorage.removeItem('firebase:authUser');
      sessionStorage.clear();
      
      // Now sign in with new credentials
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      if (shouldLogVerbose()) {
        console.log('✅ Login successful:', {
          email: userCredential.user.email,
          uid: userCredential.user.uid
        });
      }
    } catch (error: any) {
      console.error('❌ Login failed:', error.message);
      throw error;
    }
  };

  const register = async (email: string, password: string) => {
    // Use backend endpoint to create Firebase user (handles orphaned users)
    // This only creates the Firebase user - backend registration must be done separately
    try {
      const response = await api.post('/auth/create-firebase-user', { email, password });
      const { firebaseUid } = response.data;
      
      // Sign in with the created Firebase user
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error: any) {
      // If backend creation fails with "already exists", don't fallback - throw error
      if (error.response?.status === 400 && error.response?.data?.error?.includes('already exists')) {
        throw new Error(error.response.data.error);
      }
      // For other errors, try direct Firebase creation as fallback
      // (in case backend is unavailable, though this won't handle orphaned users)
      await createUserWithEmailAndPassword(auth, email, password);
    }
  };

  const logout = async () => {
    // Log logout in development and staging
    if (shouldLogVerbose()) {
      console.log('🚪 Logging out - clearing auth state...');
    }
    
    // Clear backend user state first
    setBackendUser(null);
    setUser(null);
    
    // Sign out from Firebase
    try {
      await signOut(auth);
      if (shouldLogVerbose()) {
        console.log('✅ Firebase signOut completed');
      }
    } catch (error) {
      console.error('Error signing out from Firebase:', error);
    }
    
    // Clear only auth-related storage items (not ALL storage)
    // This preserves user preferences and other non-auth data
    try {
      // Clear Firebase-specific storage keys
      const firebaseKeys = Object.keys(localStorage).filter(
        key => key.startsWith('firebase:') || 
               key.startsWith('firebaseui:') ||
               key.includes('auth') ||
               key.includes('token')
      );
      firebaseKeys.forEach(key => localStorage.removeItem(key));
      
      if (shouldLogVerbose()) {
        console.log('✅ Auth-related localStorage cleared:', firebaseKeys.length, 'keys');
      }
      
      // Clear sessionStorage (typically safe to clear entirely)
      sessionStorage.clear();
      if (shouldLogVerbose()) {
        console.log('✅ sessionStorage cleared');
      }
      
      // Only clear Firebase-related IndexedDB databases
      if ('indexedDB' in window) {
        try {
          const databases = await indexedDB.databases();
          const firebaseDbs = databases.filter(db => 
            db.name?.includes('firebase') || 
            db.name?.includes('firebaseLocalStorage')
          );
          
          await Promise.all(
            firebaseDbs.map(db => {
              if (db.name) {
                return new Promise<void>((resolve) => {
                  const deleteReq = indexedDB.deleteDatabase(db.name!);
                  deleteReq.onsuccess = () => resolve();
                  deleteReq.onerror = () => resolve(); // Resolve even on error
                  deleteReq.onblocked = () => resolve();
                });
              }
            })
          );
          if (shouldLogVerbose()) {
            console.log('✅ Firebase IndexedDB cleared:', firebaseDbs.length, 'databases');
          }
        } catch (error) {
          // IndexedDB operations can fail in some browsers - that's ok
          if (shouldLogVerbose()) {
            console.warn('⚠️ Could not clear IndexedDB:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error clearing storage:', error);
    }
    
    // Redirect to login page
    if (shouldLogVerbose()) {
      console.log('🔄 Redirecting to login...');
    }
    window.location.href = '/store';
  };

  const value: AuthContextType = {
    user,
    backendUser,
    loading,
    login,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
